import React from 'react';
import {
  Avatar,
  Box,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { motion } from 'motion/react';
import UserIdentity, { getImageUrl } from './UserIdentity';

function formatRelativeTime(value) {
  if (!value) return '';
  const diff = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function EventPreview({ event, onClick }) {
  const photos = Array.isArray(event.photos) ? event.photos : [];
  const visiblePhotos = photos.slice(0, 3);
  const moreCount = Math.max(0, photos.length - visiblePhotos.length);
  const comments = event.updates?.length || 0;
  const hearts = event.heartedBy?.length || 0;
  const poster = typeof event.userId === 'object'
    ? event.userId
    : { _id: event.userId, username: event.username };

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onClick(event)}
      sx={{
        width: '100%',
        display: 'block',
        p: 0,
        mb: 0,
        textAlign: 'left',
        color: 'inherit',
        bgcolor: 'transparent',
        border: 0,
        borderBottom: '1px solid rgba(51,27,101,0.12)',
        cursor: 'pointer',
        font: 'inherit',
        '&:hover': { bgcolor: 'rgba(89,69,140,0.055)' },
        '&:active': { bgcolor: 'rgba(89,69,140,0.1)' }
      }}
    >
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 0.75,
            color: '#16131c',
            fontWeight: 900,
            lineHeight: 1.2
          }}
        >
          {event.title || 'Clearance find'}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0
          }}
        >
          <Box sx={{ flexShrink: 0, maxWidth: { xs: 116, sm: 160 }, overflow: 'hidden' }}>
            <UserIdentity
              user={poster}
              size="sm"
              variant="light"
              showStats={false}
              dense
            />
          </Box>

          {visiblePhotos.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              {visiblePhotos.map((photo, index) => {
                const src = getImageUrl(photo?.url || photo);
                const showMore = index === visiblePhotos.length - 1 && moreCount > 0;
                return (
                  <Box
                    key={photo?.key || photo?.url || index}
                    sx={{
                      position: 'relative',
                      width: { xs: 38, sm: 44 },
                      height: { xs: 38, sm: 44 },
                      overflow: 'hidden',
                      borderRadius: 1,
                      border: '1px solid rgba(51,27,101,0.18)',
                      bgcolor: '#ece9f0'
                    }}
                  >
                    {src ? (
                      <Box
                        component="img"
                        src={src}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <Avatar variant="rounded" sx={{ width: '100%', height: '100%' }} />
                    )}
                    {showMore && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: 'rgba(17,10,35,0.62)'
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 900 }}>
                          +{moreCount}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {formatRelativeTime(event.createdAt)}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 0 }}>
            <CommentIcon sx={{ fontSize: 18, color: '#9C83C7' }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{comments}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 0 }}>
            <FavoriteIcon sx={{ fontSize: 18, color: hearts ? '#B5506B' : '#aaa' }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{hearts}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function StoreEventsSheet({ store, events, onSelectEvent, onClose }) {
  const sortedEvents = [...(events || [])].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  return (
    <motion.div
      initial={{ y: '100%', opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: '100%', opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      style={{
        position: 'fixed',
        left: '1%',
        right: '1%',
        bottom: 0,
        width: '98%',
        height: '82%',
        zIndex: 10003,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.98)',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        boxShadow: '0 -8px 30px rgba(17,10,35,0.26)'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          px: 2,
          pt: 1,
          pb: 1.25,
          flexShrink: 0,
          borderBottom: '1px solid rgba(51,27,101,0.12)'
        }}
      >
        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#c7c2ce', mx: 'auto', mb: 1 }} />
        <Typography
          variant="h6"
          sx={{
            pr: 5,
            fontWeight: 900,
            color: '#20162f',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {store?.name || 'Clearance events'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {sortedEvents.length} active clearance {sortedEvents.length === 1 ? 'event' : 'events'} · newest first
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close event list"
          sx={{ position: 'absolute', right: 8, top: 20 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 'env(safe-area-inset-bottom)' }}>
        {sortedEvents.map((event) => (
          <EventPreview key={event._id} event={event} onClick={onSelectEvent} />
        ))}
      </Box>
    </motion.div>
  );
}
